import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpHeaders } from "@angular/common/http";
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, enableProdMode, NgModule } from "@angular/core";
import { BrowserModule, Meta } from "@angular/platform-browser";

import { OKTA_CONFIG, OktaAuthModule } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { propagation, ROOT_CONTEXT } from '@opentelemetry/api';
import { getSanitizedReferer } from '@weps/shared-http';

import { environment } from "@environments/environment";
import { ENVIRONMENT_CONFIG, HttpOktaService, HttpSiteminderService, UrlService, WebLogger, WindowRef, sessionAuthorizationFactory } from "@weps/shared-http";

import { GatekeeperService } from "@services/gatekeeper/gatekeeper.service";
import { HttpInterceptors } from '@services/http-interceptor';
import { CookieFilterInterceptor } from './interceptors/cookie-filter.interceptor';
import { HttpLoggingInterceptor } from './interceptors/http-logging.interceptor';
import { OtelPropagationInterceptor } from './interceptors/otel-propagation.interceptor';
import { UtilityService } from "@services/utility.service";
import { VexService } from "@services/vex/vex.service";
import {Permissions} from "@services/authentication/auth0guard";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { LinkModule } from "@vg-constellation/angular-17/link";
import { ModalDialogModule } from "@vg-constellation/angular-17/modal-dialog";
import {firstValueFrom} from "rxjs";

const oktaAuth = new OktaAuth(environment.oidc);
const isInternal: boolean = environment.internal;

const externalProviders = [
  {
    provide: ENVIRONMENT_CONFIG,
    useValue: environment,
  },
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: baggageInitializerFactory,
    deps: [],
  },
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: sessionAuthorizationFactory,
    deps: [HttpClient, WindowRef, Meta, ENVIRONMENT_CONFIG],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: OtelPropagationInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpLoggingInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: CookieFilterInterceptor,
    multi: true,
  },
  {
    provide: "IHttpService",
    useClass: HttpSiteminderService,
  },
  UrlService,
  VexService,
  UtilityService,
  GatekeeperService,
  WindowRef,
  Permissions
];

const internalProviders = [
  {
    provide: ENVIRONMENT_CONFIG,
    useValue: environment,
  },
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: baggageInitializerFactory,
    deps: [],
  },
  {
    provide: OKTA_CONFIG,
    useValue: {oktaAuth},
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: OtelPropagationInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpLoggingInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpInterceptors,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: CookieFilterInterceptor,
    multi: true,
  },
  {
    provide: "IHttpService",
    useClass: HttpOktaService,
  },
  UrlService,
  VexService,
  UtilityService,
  GatekeeperService
];

const externalModules = [
  BrowserModule,
  HttpClientModule,
  AppRoutingModule,
  LinkModule,
  ModalDialogModule
];

const internalModules = [
  BrowserModule,
  HttpClientModule,
  AppRoutingModule,
  LinkModule,
  ModalDialogModule,
  OktaAuthModule
];

if (environment.production) {
  enableProdMode();
}

// Factory function to initialize baggage context before any HTTP requests
function baggageInitializerFactory(): () => Promise<void> {
  return () => initializeBaggage();
}

/**
 * Initializes OpenTelemetry baggage context with client POID, referer, and call chain.
 * Must run in APP_INITIALIZER to ensure baggage is available before any HTTP requests.
 *
 * Baggage propagates:
 * - 'cl' (client POID): Extracted from cookies for user identification
 * - 're' (referer): Sanitized URL (no query params/hash to prevent PII leakage)
 * - 'cc' (call chain): Tracks request path (UI → ROB → VEX)
 */
async function initializeBaggage(): Promise<void> {
  const poid = extractPoid();

  if (!poid) {
    return;
  }

  // TODO: Migrate to JWT-based authentication for POID extraction
  // instead of relying on cookies. This will improve security and
  // simplify session management across services.

  const baggageValues = {
    'cl': { value: poid },
    're': { value: getSanitizedReferer() },  // Sanitized: no query params or hash fragments
    'cc': { value: 'UI' }
  };

  const baggage = propagation.createBaggage(baggageValues);
  const baggageContext = propagation.setBaggage(ROOT_CONTEXT, baggage);

  // TODO: Evaluate whether to expose trace ID in UI for user-facing
  // error reports. Consider security implications and support workflow.
  (window as any).__OTEL_BAGGAGE_CONTEXT__ = baggageContext;
}

/**
 * Extracts POID from cookies (same logic as AppComponent)
 */
function extractPoid(): string | null {
  const cookies = document.cookie.split(";");

  const poid = getCookieValue(cookies, "POID");
  if (poid) return poid;

  const clSpoid = getCookieValue(cookies, "cl_spoid");
  if (clSpoid) return clSpoid;

  return extractPoidFromCccSharedData(cookies);
}

function getCookieValue(cookies: string[], name: string): string | null {
  const cookie = cookies.find((c) => c.split("=")[0].trim() === name);
  if (!cookie) return null;

  const value = cookie.split("=")[1];
  return value ? value.trim() : null;
}

function extractPoidFromCccSharedData(cookies: string[]): string | null {
  const cookie = cookies.find((c) => c.split("=")[0].trim() === "cccSharedData");
  if (!cookie) return null;

  const cookieValue = cookie.split("=")[1];
  if (!cookieValue) return null;

  const poid = cookieValue.split("|")[0];
  return poid ? poid.trim() : null;
}

@NgModule({
  declarations: [AppComponent],
  imports: isInternal ? internalModules : externalModules,
  providers: isInternal ? internalProviders : externalProviders,
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
